import { useTheme } from '@emotion/react';
import isNil from 'lodash/isNil';
import { AnimatePresence, motion } from 'motion/react';
import { useState } from 'react';

import { cx } from '@hrworks/design-system/functions';
import { extractNumbersFromString } from '@hrworks/sui-shared/functions';

import { Icon } from '../Icon';
import { Title } from '../Title';
import { S } from './Section.styles';
import type { SectionProps } from './Section.types';

export const Section = ({
  children,
  collapsible,
  expanded,
  defaultExpanded,
  divider,
  title,
  titleChildren,
  uppercase,
  onExpandedChange,
  id,
  alignTitle = 'start',
  breakTitleChildrenWithTitle,
  className,
  typography = 'headingMdSemibold',
  slots,
  slotProps,
  ...otherProps
}: SectionProps) => {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const _expanded = expanded ?? internalExpanded;

  const contentId = `${id}-content`;
  const titleId = `${id}-title`;

  const sectionAnimations = {
    expanded: { height: 'auto' },
    closed: { height: 0 },
  };

  const Toggle = slots?.toggle || S.SectionToggle;
  const ToggleIcon = slots?.toggleIcon || Icon;
  const SectionTitle = slots?.title || S.Title;
  const NonCollapsibleTitle = slots?.title || Title;
  const SectionDivider = slots?.divider || S.Divider;

  const childrenJSX = children && (
    <S.ChildrenContainer className={S.cls.ChildrenContainer}>{children}</S.ChildrenContainer>
  );
  const dividerJSX = divider && <SectionDivider className={S.cls.Divider} {...slotProps?.divider} />;

  const animationDuration = Number(extractNumbersFromString(useTheme().variables.animationDuration.normal));

  return children && collapsible ? (
    <S.Section className={cx(S.cls.Section, className)} {...otherProps}>
      <Toggle
        className={S.cls.SectionToggle}
        {...(!slots?.toggle && { variant: 'subtle' })}
        aria-expanded={_expanded}
        aria-controls={contentId}
        aria-label={title}
        onClick={() => {
          onExpandedChange?.(!_expanded);
          isNil(expanded) && setInternalExpanded(!internalExpanded);
        }}
        {...slotProps?.toggle}
      >
        <S.IconWrapper className={S.cls.IconWrapper} expanded={_expanded}>
          <ToggleIcon {...(!slots?.toggleIcon && { name: 'section-toggle' })} {...slotProps?.toggleIcon} />
        </S.IconWrapper>
      </Toggle>
      <SectionTitle
        id={titleId}
        className={S.cls.Title}
        {...(!slots?.title && {
          titleChildren,
          uppercase,
          alignTitle,
          typography,
          breakTitleChildrenWithChildren: breakTitleChildrenWithTitle,
        })}
        {...slotProps?.title}
      >
        {title}
      </SectionTitle>
      {dividerJSX}
      <S.MotionDiv
        className={S.cls.MotionDiv}
        animate={_expanded ? 'expanded' : 'closed'}
        variants={sectionAnimations}
        transition={{ duration: animationDuration }}
        role="region"
        aria-labelledby={titleId}
        id={contentId}
      >
        <AnimatePresence>
          {_expanded && (
            <motion.div exit={{ opacity: 0 }} transition={{ duration: animationDuration }}>
              {childrenJSX}
            </motion.div>
          )}
        </AnimatePresence>
      </S.MotionDiv>
    </S.Section>
  ) : (
    <section className={className} {...otherProps}>
      <NonCollapsibleTitle
        id={titleId}
        {...(!slots?.title && {
          titleChildren,
          uppercase,
          alignTitle,
          typography,
          breakTitleChildrenWithChildren: breakTitleChildrenWithTitle,
        })}
        {...slotProps?.title}
      >
        {title}
      </NonCollapsibleTitle>
      {dividerJSX}
      {childrenJSX}
    </section>
  );
};
